import { useState, memo } from 'react'

import isEqual from 'react-fast-compare'
import {
  LineChart as RechartsLineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'

import { AnalyticsResponse } from '@tribeplatform/gql-client/types'

import type { GroupBy, ChartTimeFrame } from '../types/index.js'
import {
  DefaultChartColors,
  LINE_INACTIVE_OPACITY,
  CHART_ANIMATION_ACTIVE,
  CHART_ANIMATION_DURATION,
} from '../utils/constants.js'
import {
  analyticsNumberFormatter,
  rechartsLineChartParser,
} from '../utils/parser.js'

import { CustomLegend } from './CustomLegend.js'
import { CustomToolTipRechartsHOC } from './CustomToolTipRecharts.js'

interface Props {
  data: {
    chartData: AnalyticsResponse
    timeFrame: ChartTimeFrame
    indexBy: string[]
    labels: string[]
    groupBy?: GroupBy[]
    ColorTheme?: string[]
  }
}

export const LineChartRecharts = memo(
  ({
    data: {
      chartData,
      timeFrame,
      indexBy,
      labels,
      groupBy,
      ColorTheme = DefaultChartColors,
    },
  }: Props) => {
    const [activeIndex, setActiveIndex] = useState(-1)

    const getOpacity = (index: number) => {
      if (activeIndex === -1) {
        return 1
      }

      if (index === activeIndex) {
        return 1
      }

      return LINE_INACTIVE_OPACITY
    }

    return (
      <>
        <CustomLegend
          labels={indexBy}
          colors={ColorTheme}
          className="mt-2"
          setActiveIndex={setActiveIndex}
        />
        <ResponsiveContainer width="99%" height={320}>
          <RechartsLineChart
            data={rechartsLineChartParser(
              chartData,
              indexBy,
              timeFrame,
              groupBy,
            )}
            margin={{ top: 20, left: 0, right: 0, bottom: 20 }}
          >
            <CartesianGrid
              strokeDasharray="0"
              vertical={false}
              stroke="#EAF0F4"
            />
            <XAxis
              tick={{ fontSize: 12 }}
              tickMargin={12}
              dataKey="date"
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tickMargin={0}
              width={30}
              // fontSize is being overridden by tribe's global selectors
              // so we have to use style here to set the fontsize
              // tick={{ fontSize: 12 }}
              style={{ fontSize: 12 }}
              tickFormatter={value => analyticsNumberFormatter(value)}
            />
            <Tooltip
              content={CustomToolTipRechartsHOC({ labels })}
              cursor={{
                stroke: '#C6C6D2',
                strokeDasharray: '5 5',
              }}
            />
            {indexBy.map((label, index) => (
              <Line
                key={label}
                type="linear"
                dataKey={label}
                dot={{ r: 0 }}
                strokeWidth={2}
                stroke={ColorTheme[index]}
                isAnimationActive={CHART_ANIMATION_ACTIVE}
                animationDuration={CHART_ANIMATION_DURATION}
                activeDot={{ r: 5 }}
                opacity={getOpacity(index)}
              />
            ))}
          </RechartsLineChart>
        </ResponsiveContainer>
      </>
    )
  },
  isEqual,
)
